import React, { forwardRef, useImperativeHandle } from "react";

import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import IconButton from "@mui/material/IconButton";
import { Box, Typography, useMediaQuery } from "@mui/material";
import IosShareIcon from "@mui/icons-material/IosShare";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import Add from "@mui/icons-material/Add";
import Audiotrack from "@mui/icons-material/Audiotrack";
import FormatUnderlined from "@mui/icons-material/Subtitles";
import TextIcon from "@mui/icons-material/TextFields";
import SourceIcon from "@mui/icons-material/Source";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import FilterIcon from "@mui/icons-material/Tune";
import MenuIcon from "@mui/icons-material/Menu";

import {
  EpisodesIcon,
  UploadEpisodeIcon,
  ProjectsFolderIcon,
  ShareNetworkIcon,
  UsersIcon,
} from "../icons";
import SidebarList from "./components/SidebarList";
import MobileSidebar from "./components/MobileSidebar";
import { SidebarListProps } from "./types";
import { SidebarContent } from "./components/SidebarContent";
import { RoleName } from "../enums/RoleName";
import RoleBasedAccess from "../RoleBasedAccess";

const COLLAPSED_WIDTH = 72;
const EXPANDED_WIDTH = 260;

const SIDEBAR_BG = "#FFFFFF";
const SIDEBAR_BORDER = "rgba(0, 0, 0, 0.08)";

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ open }) => ({
  "& .MuiDrawer-paper": {
    backgroundColor: SIDEBAR_BG,
    border: "none",
    borderRight: `1px solid ${SIDEBAR_BORDER}`,
    position: "relative",
    whiteSpace: "nowrap",
    width: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
    transition: "width 250ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
    boxSizing: "border-box",
    overflowX: "hidden",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
}));

interface SidebarProps {
  pageName: string;
  expended?: boolean;
  editFileSidebar?: boolean;
  hidePrimary?: boolean;
  selectedSidebarItem?: string;
  onSelectSidebarItem?: React.Dispatch<React.SetStateAction<string>>;
}

export interface SidebarRef {
  toggleMobile: () => void;
}

const SidebarView = forwardRef<SidebarRef, SidebarProps>(
  (
    {
      pageName,
      expended = true,
      editFileSidebar = false,
      hidePrimary = false,
      selectedSidebarItem,
      onSelectSidebarItem,
    },
    ref,
  ) => {
    const globalTheme = useTheme();
    const mobile = useMediaQuery(globalTheme.breakpoints.down("md"));
    const newExpendedState = mobile ? false : expended;

    const [open, setOpen] = React.useState(() => {
      if (mobile) return false;
      const savedState = sessionStorage.getItem("sidebarOpen");
      return savedState !== null ? JSON.parse(savedState) : newExpendedState;
    });

    const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

    useImperativeHandle(ref, () => ({
      toggleMobile: () => setMobileSidebarOpen(!mobileSidebarOpen),
    }));

    React.useEffect(() => {
      if (!mobile) {
        sessionStorage.setItem("sidebarOpen", JSON.stringify(open));
      }
    }, [open, mobile]);

    const handleToggleSidebar = () => {
      setOpen(!open);
    };

    const handleMobileToggle = () => {
      setMobileSidebarOpen(!mobileSidebarOpen);
    };

    const uploadEpisodeItem: SidebarListProps = {
      icon: <UploadEpisodeIcon />,
      name: "Upload Episode",
      link: "/episodes/upload",
      isSelected: pageName === "Upload Episode",
    };

    const sidebarListWithoutUpload: SidebarListProps[] = [
      {
        icon: <EpisodesIcon />,
        name: "Episodes",
        link: "/episodes",
        isSelected: pageName === "Episodes",
        permissions: [RoleName.ADMIN, RoleName.EDITOR, RoleName.VIEWER],
      },
      {
        icon: <UploadEpisodeIcon />,
        name: "Upload Episode",
        link: "/episodes/upload",
        isSelected: pageName === "Upload Episode",
        permissions: [RoleName.ADMIN, RoleName.EDITOR],
      },
      {
        icon: <ProjectsFolderIcon />,
        name: "Projects",
        link: "/projects",
        isSelected: pageName === "Projects",
        permissions: [RoleName.ADMIN, RoleName.EDITOR, RoleName.VIEWER],
      },
      {
        icon: <ShareNetworkIcon />,
        name: "Shared Episodes",
        link: "/shared-episodes",
        disabled: true,
        isSelected: false,
        permissions: [RoleName.ADMIN, RoleName.EDITOR, RoleName.VIEWER],
        subMenu: [
          {
            icon: <IosShareIcon />,
            name: "Shared Episodes",
            link: "/shared-episodes",
            isSelected: pageName === "Shared Episodes",
          },
          {
            icon: <CoPresentIcon />,
            name: "Episodes Shared With Me",
            link: "/episodes-shared-with-me",
            isSelected: pageName === "Episodes Shared With Me",
          },
        ],
      },
    ];

    const sidebarList2 = [
      { icon: <Add sx={{ width: "100%" }} />, name: "Media", disabled: true },
      {
        icon: <Audiotrack sx={{ width: "100%" }} />,
        name: "Audio",
        disabled: true,
      },
      { icon: <FormatUnderlined sx={{ width: "100%" }} />, name: "Captions" },
      { icon: <TextIcon sx={{ width: "100%" }} />, name: "Text" },
      { icon: <SourceIcon sx={{ width: "100%" }} />, name: "Elements" },
      {
        icon: <LinearScaleIcon sx={{ width: "100%" }} />,
        name: "Transitions",
        disabled: true,
      },
      {
        icon: <FilterIcon sx={{ width: "100%" }} />,
        name: "Filters",
        disabled: true,
      },
    ];

    const sidebarBottomList: SidebarListProps[] = [
      {
        icon: <UsersIcon />,
        name: "Users",
        link: "/users",
        isSelected: pageName === "Users",
        permissions: [RoleName.ADMIN, RoleName.EDITOR],
      },
    ];

    if (mobile) {
      return (
        <>
          <Box
            sx={{
              position: "fixed",
              top: { xs: 8, sm: 12 },
              left: { xs: 8, sm: 12 },
              zIndex: 1200,
              display: { xs: "block", md: "none" },
            }}
          >
            <IconButton
              onClick={handleMobileToggle}
              sx={{
                color: globalTheme.palette.text.secondary,
                backgroundColor: "#ffffff",
                border: `1px solid ${SIDEBAR_BORDER}`,
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                "&:hover": { backgroundColor: "#f5f7fa" },
              }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
          <MobileSidebar
            open={mobileSidebarOpen}
            onClose={() => setMobileSidebarOpen(false)}
            pageName={pageName}
          />
        </>
      );
    }

    return (
      <>
        <Drawer hidden={hidePrimary} variant="permanent" open={open}>
          {/* Logo area */}
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              px: open ? 2.5 : 1.5,
              minHeight: "72px !important",
              backgroundColor: SIDEBAR_BG,
              borderBottom: `1px solid ${SIDEBAR_BORDER}`,
              justifyContent: open ? "space-between" : "center",
              flexShrink: 0,
            }}
          >
            {open ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1.5,
                  overflow: "hidden",
                }}
              >
                <img
                  src="/logo.png"
                  alt="Podcast Assistant Logo"
                  style={{
                    width: 36,
                    height: 36,
                    display: "block",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    color: globalTheme.palette.text.primary,
                    lineHeight: 1.3,
                    letterSpacing: "-0.01em",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Podcast Assistant
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => setOpen(true)}
              >
                <img
                  src="/logo.png"
                  alt="Podcast Assistant Logo"
                  style={{ width: 36, height: 36, display: "block" }}
                />
              </Box>
            )}
            {open && (
              <IconButton
                onClick={handleToggleSidebar}
                sx={{
                  color: globalTheme.palette.text.secondary,
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  flexShrink: 0,
                  border: `1px solid ${SIDEBAR_BORDER}`,
                  "&:hover": {
                    backgroundColor: globalTheme.palette.action.hover,
                    color: globalTheme.palette.primary.main,
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Toolbar>

          {/* MENU label */}
          {open && (
            <Box sx={{ px: 2.5, pt: 2.5, pb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: globalTheme.palette.text.secondary,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Menu
              </Typography>
            </Box>
          )}

          {/* Nav items */}
          <Box sx={{ flex: 1, overflow: "auto", overflowX: "hidden" }}>
            <SidebarList
              sidebarList={sidebarListWithoutUpload}
              open={open}
              mobile={false}
            />
            <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
              <SidebarList
                sidebarList={[uploadEpisodeItem]}
                open={open}
                mobile={false}
              />
            </RoleBasedAccess>
          </Box>

          {/* Bottom items */}
          <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
            <Box
              sx={{
                width: "100%",
                backgroundColor: SIDEBAR_BG,
                borderTop: `1px solid ${SIDEBAR_BORDER}`,
                flexShrink: 0,
              }}
            >
              <SidebarList
                sidebarList={sidebarBottomList}
                open={open}
                mobile={false}
              />
            </Box>
          </RoleBasedAccess>
        </Drawer>
        {editFileSidebar ? (
          <Box sx={{ position: "relative" }}>
            <SidebarContent
              sidebarContent={sidebarList2 as SidebarListProps[]}
              selectedItem={selectedSidebarItem}
              onSelectItem={(name) => onSelectSidebarItem?.(name)}
              showLogo
            />
          </Box>
        ) : null}
      </>
    );
  },
);

export default SidebarView;
